import { useState, type FormEvent } from 'react';
import { useKnowledgeCrawlsTab } from '../../../features/knowledge/hooks/useKnowledgeCrawlsTab';
import type { CrawlJob, WithResourceId } from '../../../types/knowledge';
import { CrawlJobCard } from '../CrawlJobCard';
import { KnowledgeCrawlsDialogs } from './KnowledgeCrawlsDialogs';
import { KnowledgeCrawlsSections } from './KnowledgeCrawlsSections';
import { KnowledgeCrawlsToolbar } from './KnowledgeCrawlsToolbar';
import '../knowledgeDatabaseShared.css';
import './KnowledgeCrawlsTab.css';

type KnowledgeCrawlsTabProps = {
  databaseId: string;
  canManageCrawls: boolean;
};

export function KnowledgeCrawlsTab({
  databaseId,
  canManageCrawls,
}: KnowledgeCrawlsTabProps) {
  const model = useKnowledgeCrawlsTab({ databaseId, canManageCrawls });
  const [isStartCrawlModalOpen, setIsStartCrawlModalOpen] = useState(false);
  const {
    translate,
    crawls,
    liveStatusByCrawlId,
    filteredCrawlsMapping,
    filteredCrawlsNeedingAttention,
    filteredCrawlsInProgress,
    filteredCrawlsCompleted,
    filteredCrawlsFailed,
    visibleFilteredCrawlCount,
    crawlSearchInput,
    handleCrawlSearchChange,
    hasActiveCrawlSearch,
    crawlUrl,
    crawlMaxDepth,
    crawlMaxPages,
    crawlUseStartUrlAsPrefixFilter,
    crawlAllowSubdomains,
    crawlExcludedUrls,
    setCrawlUrl,
    setCrawlMaxDepth,
    setCrawlMaxPages,
    onCrawlMaxDepthBlur,
    onCrawlMaxPagesBlur,
    setCrawlUseStartUrlAsPrefixFilter,
    setCrawlAllowSubdomains,
    setCrawlExcludedUrls,
    resetCrawlForm,
    handleStartCrawl,
    preventInvalidNumericKey,
    isStartingCrawl,
    handleManualRefresh,
    isMappingExpanded,
    isNeedsAttentionExpanded,
    isInProgressExpanded,
    isCompletedExpanded,
    isFailedExpanded,
    toggleMappingExpanded,
    toggleNeedsAttentionExpanded,
    toggleInProgressExpanded,
    toggleCompletedExpanded,
    toggleFailedExpanded,
    expandAllCrawlGroups,
    collapseAllCrawlGroups,
    selectionCrawl,
    selectionNodes,
    selectedNodeIds,
    setSelectedNodeIds,
    isSelectionLoading,
    isSelectionSubmitting,
    isAwaitingSelectionStart,
    isScheduledAwaitingSelection,
    openSelection,
    handleCloseSelection,
    confirmSelection,
    handleRestartCrawl,
    handleRetryFailedCrawl,
    handleResumeAbandonedCrawl,
    handleRequestDeleteCrawl,
    openScheduleModal,
    scheduleModalCrawl,
    scheduleModalExisting,
    handleSaveSchedule,
    handleCloseScheduleModal,
    isScheduleSubmitting,
    pendingScheduleDelete,
    setPendingScheduleDelete,
    handleDeleteSchedule,
    pendingDelete,
    setPendingDelete,
    confirmDeleteCrawl,
    isDeletingCrawl,
    formatCrawlStatus,
    locale,
    directoryUsersById,
    crawlScheduleByJobId,
  } = model;

  const renderCrawlListItem = (crawl: WithResourceId<CrawlJob>) => {
    const liveStatus = liveStatusByCrawlId.get(crawl.id);
    const statusForSelection = liveStatus?.status ?? crawl.status;

    return (
      <CrawlJobCard
        key={crawl.id}
        crawl={crawl}
        liveStatus={liveStatus}
        canManageCrawls={canManageCrawls}
        schedule={crawlScheduleByJobId.get(crawl.id)}
        locale={locale}
        directoryUsersById={directoryUsersById}
        translate={translate}
        formatCrawlStatus={formatCrawlStatus}
        onSelection={() =>
          openSelection(
            statusForSelection != null
              ? { ...crawl, status: statusForSelection }
              : crawl,
          )
        }
        onSchedule={() => openScheduleModal(crawl)}
        onRestart={() => void handleRestartCrawl(crawl)}
        onRetry={() => void handleRetryFailedCrawl(crawl)}
        onResumeAbandoned={() => void handleResumeAbandonedCrawl(crawl)}
        onDelete={() => handleRequestDeleteCrawl(crawl)}
      />
    );
  };

  const handleCloseStartCrawlModal = () => {
    if (isStartingCrawl) {
      return;
    }
    resetCrawlForm();
    setIsStartCrawlModalOpen(false);
  };

  const handleStartCrawlSubmit = (event: FormEvent<HTMLFormElement>) => {
    handleStartCrawl(event, {
      onSuccess: () => {
        setIsStartCrawlModalOpen(false);
      },
    });
  };

  const areAllCrawlGroupsExpanded =
    isMappingExpanded &&
    isNeedsAttentionExpanded &&
    isInProgressExpanded &&
    isCompletedExpanded &&
    isFailedExpanded;

  let crawlListBody;
  if (crawls.length === 0) {
    crawlListBody = (
      <p className="knowledge-database__empty">
        {translate('knowledge.crawlsEmpty')}
      </p>
    );
  } else if (hasActiveCrawlSearch && visibleFilteredCrawlCount === 0) {
    crawlListBody = (
      <p className="knowledge-database__empty">
        {translate('knowledge.crawlsNoSearchResults')}
      </p>
    );
  } else {
    crawlListBody = (
      <KnowledgeCrawlsSections
        filteredCrawlsMapping={filteredCrawlsMapping}
        filteredCrawlsNeedingAttention={filteredCrawlsNeedingAttention}
        filteredCrawlsInProgress={filteredCrawlsInProgress}
        filteredCrawlsCompleted={filteredCrawlsCompleted}
        filteredCrawlsFailed={filteredCrawlsFailed}
        isMappingExpanded={isMappingExpanded}
        isNeedsAttentionExpanded={isNeedsAttentionExpanded}
        isInProgressExpanded={isInProgressExpanded}
        isCompletedExpanded={isCompletedExpanded}
        isFailedExpanded={isFailedExpanded}
        translate={translate}
        onToggleMapping={toggleMappingExpanded}
        onToggleNeedsAttention={toggleNeedsAttentionExpanded}
        onToggleInProgress={toggleInProgressExpanded}
        onToggleCompleted={toggleCompletedExpanded}
        onToggleFailed={toggleFailedExpanded}
        renderCrawlListItem={renderCrawlListItem}
      />
    );
  }

  return (
    <>
      <section className="knowledge-database__panel">
        <KnowledgeCrawlsToolbar
          canManageCrawls={canManageCrawls}
          crawlSearchInput={crawlSearchInput}
          crawlsCount={crawls.length}
          areAllCrawlGroupsExpanded={areAllCrawlGroupsExpanded}
          translate={translate}
          onCrawlSearchChange={handleCrawlSearchChange}
          onStartCrawl={() => setIsStartCrawlModalOpen(true)}
          onManualRefresh={handleManualRefresh}
          onExpandAll={expandAllCrawlGroups}
          onCollapseAll={collapseAllCrawlGroups}
        />
        {crawlListBody}
      </section>

      <KnowledgeCrawlsDialogs
        translate={translate}
        selectionCrawl={selectionCrawl}
        selectionNodes={selectionNodes}
        selectedNodeIds={selectedNodeIds}
        setSelectedNodeIds={setSelectedNodeIds}
        confirmSelection={confirmSelection}
        handleCloseSelection={handleCloseSelection}
        isAwaitingSelectionStart={isAwaitingSelectionStart}
        isScheduledAwaitingSelection={isScheduledAwaitingSelection}
        isSelectionSubmitting={isSelectionSubmitting}
        isSelectionLoading={isSelectionLoading}
        isStartCrawlModalOpen={isStartCrawlModalOpen}
        crawlUrl={crawlUrl}
        crawlMaxDepth={crawlMaxDepth}
        crawlMaxPages={crawlMaxPages}
        crawlUseStartUrlAsPrefixFilter={crawlUseStartUrlAsPrefixFilter}
        crawlAllowSubdomains={crawlAllowSubdomains}
        crawlExcludedUrls={crawlExcludedUrls}
        setCrawlUrl={setCrawlUrl}
        setCrawlMaxDepth={setCrawlMaxDepth}
        setCrawlMaxPages={setCrawlMaxPages}
        onCrawlMaxDepthBlur={onCrawlMaxDepthBlur}
        onCrawlMaxPagesBlur={onCrawlMaxPagesBlur}
        setCrawlUseStartUrlAsPrefixFilter={setCrawlUseStartUrlAsPrefixFilter}
        setCrawlAllowSubdomains={setCrawlAllowSubdomains}
        setCrawlExcludedUrls={setCrawlExcludedUrls}
        handleStartCrawlSubmit={handleStartCrawlSubmit}
        handleCloseStartCrawlModal={handleCloseStartCrawlModal}
        isStartingCrawl={isStartingCrawl}
        preventInvalidNumericKey={preventInvalidNumericKey}
        scheduleModalCrawl={scheduleModalCrawl}
        scheduleModalExisting={scheduleModalExisting}
        handleSaveSchedule={handleSaveSchedule}
        setPendingScheduleDelete={setPendingScheduleDelete}
        handleCloseScheduleModal={handleCloseScheduleModal}
        isScheduleSubmitting={isScheduleSubmitting}
        pendingScheduleDelete={pendingScheduleDelete}
        handleDeleteSchedule={handleDeleteSchedule}
        pendingDelete={pendingDelete}
        setPendingDelete={setPendingDelete}
        confirmDeleteCrawl={confirmDeleteCrawl}
        isDeletingCrawl={isDeletingCrawl}
      />
    </>
  );
}
